import useGetLabourForecastForProject, {
  LabourForecast,
} from '@/api/queries/project/useGetLabourForecastForProject';
import useGetProjectProfitBreakdown from '@/api/queries/project/useGetProjectProfitBreakdown';
import { GetPaginatedLabourForecastsForProjectInput } from '@/gql/graphql';
import { humanizeCurrency } from '@/helpers/currency';
import { useUserHasPermission } from '@/hooks/usePermissions';
import { PermissionGuard } from '@/ui/components/permissionGuard/PermissionGuard';
import { Badge, Button, ManageableItemsList, Tooltip } from '@payaca/ambrosia';

import { LabourForecastPermissions } from '@payaca/permissions/labourForecast/labourForecast.permissions';
import { TimelogsPermissions } from '@payaca/permissions/timelogs/timelogs.permissions';
import { SortDirection } from '@payaca/types/listViewTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { checkUnreachable } from '@payaca/utilities/guards';
import { produce } from 'immer';
import { FC, useReducer, useState } from 'react';
import { useParams } from 'react-router';
import { usePaginationState } from '../MaterialsPanel/materialsPanelHooks';
import { CreateEditLabourForecastSidebar } from './CreateEditLabourForecastSidebar';
import DeleteLabourForecastModal from './DeleteLabourForecastModal';

const useLabourForecastInputPaginationState = (pageSize: number) => {
  const { pagination, ...paginationActions } = usePaginationState(pageSize);
  const defaultInput: GetPaginatedLabourForecastsForProjectInput = {
    sortDirection: 'DESC',
    sortBy: 'CREATED_AT',
  };
  const [input, setInput] =
    useState<GetPaginatedLabourForecastsForProjectInput>(defaultInput);

  return {
    reset: () => {
      setInput(defaultInput);
      paginationActions.setCurrentPage(1);
    },
    pagination,
    input,
    ...paginationActions,
    setSort: (sort: {
      sortDirection: GetPaginatedLabourForecastsForProjectInput['sortDirection'];
      sortBy: GetPaginatedLabourForecastsForProjectInput['sortBy'];
    }) => {
      setInput({ ...input, ...sort });
    },
  };
};

export const LabourForecastCard: FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { pagination, input, setSort, setCurrentPage } =
    useLabourForecastInputPaginationState(100);

  const { data: labourForecastPageData, isLoading } =
    useGetLabourForecastForProject(projectId, pagination, input);

  const [labourPanelState, dispatch] = useReducer(labourPanelReducer, {
    openSidebar: null,
  });

  const { openSidebar } = labourPanelState;

  const labourForecastsPage =
    labourForecastPageData?.projectById.labourForecasts;

  const showPaginationRequired =
    labourForecastsPage && labourForecastsPage.totalCount > pagination.limit;

  const { data: projectProfitBreakdownData } =
    useGetProjectProfitBreakdown(projectId);

  const labourProfitBreakdown =
    projectProfitBreakdownData?.projectById.profitBreakdown.labour;

  const canCreate = useUserHasPermission({
    permissions: [LabourForecastPermissions.CREATE_LABOUR_FORECAST],
  });

  const canEdit = useUserHasPermission({
    permissions: [LabourForecastPermissions.UPDATE_LABOUR_FORECAST],
  });

  const canDelete = useUserHasPermission({
    permissions: [LabourForecastPermissions.DELETE_LABOUR_FORECAST],
  });

  const showOverBudgetBadge =
    !!labourForecastsPage?.totalCount &&
    labourProfitBreakdown &&
    labourProfitBreakdown.actual.value > labourProfitBreakdown.forecast.value;

  return (
    <>
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Forecast"
          buttons={
            <>
              {showOverBudgetBadge && (
                <PermissionGuard
                  renderIfHasPermissions={[TimelogsPermissions.GET_TIMELOGS]}
                >
                  <Badge
                    variant="soft"
                    colour="red"
                    className="whitespace-nowrap"
                    rounded={true}
                  >
                    <UntitledIcon name="alert-triangle.3" className="w-4 h-4" />
                    Over budget
                  </Badge>
                </PermissionGuard>
              )}
              <Tooltip
                enabled={!canCreate}
                tooltipContent="You don't have permission to perform this action"
              >
                <Button
                  variant="white"
                  size="sm"
                  disabled={!canCreate}
                  onClick={
                    canCreate
                      ? () =>
                          dispatch({ type: 'openCreateLabourForecastSidebar' })
                      : undefined
                  }
                >
                  Add forecast cost
                </Button>
              </Tooltip>
            </>
          }
        />

        <ManageableItemsList.Table<LabourForecast>
          emptyText="No forecast has been added yet"
          uniqueKey="id"
          items={labourForecastsPage?.items || []}
          isLoading={isLoading}
          footerColumns={
            labourForecastsPage
              ? [
                  {
                    span: 3,
                    render: () => 'Total',
                  },
                  {
                    span: 1,
                    className: 'text-right',
                    render: () => (
                      <span className="prose">
                        {humanizeCurrency(labourForecastsPage.totalCost)}
                      </span>
                    ),
                  },
                ]
              : undefined
          }
          onClickRow={
            canEdit
              ? (labourForecast) => {
                  dispatch({
                    type: 'openEditLabourForecastSidebar',
                    selectedLabourForecast: labourForecast,
                  });
                }
              : undefined
          }
          itemActions={[
            {
              label: 'Edit',
              onClick: (labourForecast: LabourForecast) =>
                dispatch({
                  type: 'openEditLabourForecastSidebar',
                  selectedLabourForecast: labourForecast,
                }),
              isDisabled: () => !canEdit,
              disabledReasons: [
                "You don't have permission to perform this action",
              ],
            },
            {
              label: 'Delete',
              onClick: (labourForecast: LabourForecast) =>
                dispatch({
                  type: 'openConfirmDeleteLabourForecastModal',
                  selectedLabourForecast: labourForecast,
                }),
              isDisabled: () => !canDelete,
              disabledReasons: [
                "You don't have permission to perform this action",
              ],
            },
          ]}
        >
          <ManageableItemsList.Table.Column
            header="Description"
            field="description"
            render={(description) => (
              <div className="max-w-[400px] truncate">{description}</div>
            )}
          />
          <ManageableItemsList.Table.Column
            header="Time"
            field="quantity"
            render={(quantity, item) =>
              `${quantity} ${item.timeUnit.toLowerCase()}${
                quantity === 1 ? '' : 's'
              }`
            }
          />
          <ManageableItemsList.Table.Column
            header="Cost per unit"
            field="costPerTimeUnit"
            align="end"
            render={(costPerTimeUnit) => humanizeCurrency(costPerTimeUnit)}
            onSort={(field, sortDirection) => {
              setSort({
                sortBy: 'COST_PER_TIME_UNIT',
                sortDirection:
                  sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
              });
            }}
          />
          <ManageableItemsList.Table.Column
            header="Cost"
            field="totalCost"
            align="end"
            render={(totalCost) => humanizeCurrency(totalCost)}
            onSort={(field, sortDirection) => {
              setSort({
                sortBy: 'TOTAL_COST',
                sortDirection:
                  sortDirection === SortDirection.ASCENDING ? 'ASC' : 'DESC',
              });
            }}
          />
        </ManageableItemsList.Table>
        {labourForecastsPage && showPaginationRequired && (
          <ManageableItemsList.PaginationBar
            type="from-paginated-result"
            {...labourForecastsPage}
            onPageChange={setCurrentPage}
          />
        )}
      </ManageableItemsList>

      <CreateEditLabourForecastSidebar
        isOpen={openSidebar === 'create' || openSidebar === 'edit'}
        onClose={() => dispatch({ type: 'closeSidebar' })}
        labourForecast={labourPanelState.selectedLabourForecast || undefined}
      />
      {labourPanelState.selectedLabourForecast && (
        <DeleteLabourForecastModal
          isOpen={labourPanelState.openModal === 'delete'}
          onClose={() => dispatch({ type: 'closeModal' })}
          labourForecast={labourPanelState.selectedLabourForecast}
          projectId={projectId}
        />
      )}
    </>
  );
};

type LabourPanelState = {
  openSidebar?: 'create' | 'edit' | null;
  openModal?: 'delete' | null;
  selectedLabourForecast?: LabourForecast;
};

type LabourPanelAction =
  | { type: 'openCreateLabourForecastSidebar' }
  | {
      type: 'openEditLabourForecastSidebar';
      selectedLabourForecast: LabourForecast;
    }
  | {
      type: 'openConfirmDeleteLabourForecastModal';
      selectedLabourForecast: LabourForecast;
    }
  | { type: 'closeModal' }
  | { type: 'closeSidebar' };

const labourPanelReducer = (
  baseState: LabourPanelState,
  action: LabourPanelAction
) => {
  return produce(baseState, (draftState) => {
    const type = action.type;

    switch (type) {
      case 'openCreateLabourForecastSidebar':
        draftState.openSidebar = 'create';
        break;
      case 'openEditLabourForecastSidebar':
        draftState.openSidebar = 'edit';
        draftState.selectedLabourForecast = action.selectedLabourForecast;
        break;
      case 'closeSidebar':
        draftState.openSidebar = null;
        draftState.selectedLabourForecast = undefined;
        break;
      case 'openConfirmDeleteLabourForecastModal':
        draftState.openModal = 'delete';
        draftState.selectedLabourForecast = action.selectedLabourForecast;
        break;
      case 'closeModal':
        draftState.openModal = null;
        draftState.selectedLabourForecast = undefined;
        break;
      default:
        checkUnreachable(type);
        break;
    }
  });
};
